import styles from '../index.module.css'

import type { Column } from '@tanstack/react-table'
import { flexRender } from '@tanstack/react-table'
import cn from 'clsx'
import React, { CSSProperties, useMemo } from 'react'

import { TRACK } from '@disco/constants/ItemTypes'

import { Checkbox, Table } from 'components/ds'
import Draggable from 'components/ui/draggable'
import DraggableTrackPreview from 'components/ui/draggable/draggable-track-preview'

import { sanitizeColumnId } from '../table-utils'
import type { Row, WithId } from '../types'

type VisibleColumns<T> = { visibleColumns: Column<T>[] }

type TableBodyRowProps<T extends WithId> = {
  row: Row<T>
  rowActionsOpen: { id: string; menu: string } | null
  rowIsSelected: boolean
  isPartOfMultipleSelection: boolean
  isExpanded?: boolean
  handleTrackClick: (
    _event: Pick<MouseEvent, 'shiftKey' | 'metaKey' | 'ctrlKey'>,
    _row: Row<T>,
  ) => void
  selectedRowItems: Row<T>[]
  getPinningStyles: (_column: Column<T>) => CSSProperties
  location: string
  searchType?: string
  searchResultsSource?: string
} & VisibleColumns<T>

const COMPONENT_PREVIEW_STYLES: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-evenly',
}

const TableBodyRowComponent = <T extends WithId>({
  row,
  rowActionsOpen,
  visibleColumns,
  handleTrackClick,
  isPartOfMultipleSelection,
  isExpanded,
  selectedRowItems,
  getPinningStyles,
  location,
  searchType,
  searchResultsSource,
}: TableBodyRowProps<T>) => {
  const dragData = useMemo(() => {
    const rowLocation = location?.startsWith('/search')
      ? 'Search page'
      : location
    return {
      isPartOfMultipleSelection,
      selected: selectedRowItems.map(r => r.index),
      selectedItems: isPartOfMultipleSelection
        ? selectedRowItems.map(r => r.original).filter(Boolean)
        : undefined,
      track: row.original,
      location: rowLocation,
      context: rowLocation,
      searchType: searchType
        ? searchType.charAt(0).toUpperCase() + searchType.slice(1)
        : undefined,

      source:
        typeof row.original?.source === 'string'
          ? row.original.source
          : searchResultsSource,
    }
  }, [
    row.original,
    selectedRowItems,
    isPartOfMultipleSelection,
    searchType,
    searchResultsSource,
    location,
  ])

  const componentProps = useMemo(
    () => ({
      id: `search-results-track-${row.id}`,
      'data-testid': `search-track-item-${row.original.id}`,
      'data-state': row.getIsSelected() ? 'selected' : undefined,
      className: cn({
        [styles.HoveredTableRow]:
          rowActionsOpen?.id === row.id || isPartOfMultipleSelection,
        [styles.ExpandedRow]: isExpanded,
      }),
    }),
    [row, rowActionsOpen?.id, isPartOfMultipleSelection, isExpanded],
  )

  const componentPreviewStyles = COMPONENT_PREVIEW_STYLES

  const visibleCells = row
    .getVisibleCells()
    .filter(cell => visibleColumns.includes(cell.column))

  // Memoize cell styles and classNames for each cell
  const { cellStyles, cellClassNames } = useMemo(
    () => ({
      cellStyles: visibleCells.map(cell => ({
        ...getPinningStyles(cell.column),
        width: `calc(var(--col-${sanitizeColumnId(
          cell.column.id,
        )}-size) * 1px)`,
        verticalAlign: 'middle',
      })),

      cellClassNames: visibleCells.map(cell =>
        cn(styles.TableCell, {
          [styles.PinnedCell]: cell.column.getIsPinned(),
          [styles.ActionsCell]: cell.column.id === 'actions',
        }),
      ),
    }),
    [visibleCells],
  )
  return (
    <Draggable
      key={row.id}
      id={`${row.index}-track-${row.original.id}`}
      disabled={row.original?.type !== TRACK}
      data={dragData}
      type={row.original?.type ?? 'defaultType'}
      component={Table.Row}
      disableStyles={true}
      disableContentWrapper={true}
      componentProps={componentProps}
      componentPreviewStyles={componentPreviewStyles}
      preview={<DraggableTrackPreview track={row.original} />}
    >
      {visibleCells.map((cell, idx) => (
        <Table.Cell
          key={cell.column.id}
          style={{
            ...cellStyles[idx],
          }}
          className={cellClassNames[idx]}
          onClick={e => {
            if (!row.getCanSelect()) return
            if (cell.column.id === 'select') {
              // metaKey: !e.shiftKey prevents clearDataTableSelection — it only fires
              // when all three modifier flags are false. Non-shift clicks use
              // INDIVIDUAL_ITEM_SELECTION; shift clicks use RANGE_SELECTION.
              handleTrackClick(
                { shiftKey: e.shiftKey, metaKey: !e.shiftKey, ctrlKey: false },
                row,
              )
            } else if (cell.column.id !== 'actions') {
              handleTrackClick(e.nativeEvent, row)
            }
          }}
        >
          {cell.column.id === 'select' ? (
            <span
              className={
                !row.getCanSelect() ? styles.NotAllowedCursor : undefined
              }
            >
              <Checkbox
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                onCheckedChange={value => row.toggleSelected(value !== false)}
                aria-label="Select row"
              />
            </span>
          ) : (
            flexRender(cell.column.columnDef.cell, cell.getContext())
          )}
        </Table.Cell>
      ))}
    </Draggable>
  )
}

TableBodyRowComponent.displayName = 'TableBodyRow'

export const TableRow = React.memo(
  TableBodyRowComponent,
  (prevProps, nextProps) => {
    if (prevProps.visibleColumns !== nextProps.visibleColumns) {
      return false
    }
    const prevCells = prevProps.row
      .getVisibleCells()
      .filter(cell => prevProps.visibleColumns.includes(cell.column))
    const nextCells = nextProps.row
      .getVisibleCells()
      .filter(cell => nextProps.visibleColumns.includes(cell.column))

    const prevCellIds = prevCells.map(c => c.id).join(',')
    const nextCellIds = nextCells.map(c => c.id).join(',')

    const prevCellValues = prevCells.map(c => c.getValue()).join(',')
    const nextCellValues = nextCells.map(c => c.getValue()).join(',')

    const prevSelectedRowIds = prevProps.selectedRowItems
      .map(r => r.id)
      .join(',')

    const nextSelectedRowIds = nextProps.selectedRowItems
      .map(r => r.id)
      .join(',')

    return (
      prevProps.row.id === nextProps.row.id &&
      prevProps.rowActionsOpen?.id === nextProps.rowActionsOpen?.id &&
      prevProps.rowIsSelected === nextProps.rowIsSelected &&
      prevProps.isPartOfMultipleSelection ===
        nextProps.isPartOfMultipleSelection &&
      prevProps.isExpanded === nextProps.isExpanded &&
      prevProps.row.getCanSelect() === nextProps.row.getCanSelect() &&
      prevCellIds === nextCellIds &&
      prevCellValues === nextCellValues &&
      prevSelectedRowIds === nextSelectedRowIds
    )
  },
) as <T extends WithId>(
  props: TableBodyRowProps<T>,
) => React.ReactElement | null
